import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Typography, FormControl, Checkbox, ListItemText, CircularProgress, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { fetchOwnedAgencyData, removeOwnedAgencyData } from '../../redux/actions/Data';

const useStyles = makeStyles(theme => ({
  dropdownWrapper: {
    position: 'relative',
    minWidth: '190px',
    maxWidth: '190px',
    marginLeft: 'auto',
    marginBottom: '-22px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',

    '@media (max-width: 768px)': {
      marginBottom: '-14px',
    },

    '@media (max-width: 430px)': {
      minWidth: '150px',
      maxWidth: '150px',
    },
  },
  headingText: {
    margin: '0',
    fontSize: '15px',
    fontWeight: 300,
    marginLeft: '9px',

    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  checkboxRoot: {
    color: '#ccc',
    '&.Mui-checked': {
      color: '#19989B',
    },
  },
  loaderOverlay: {
    position: 'absolute',
    top: '10px',
    right: '14px',
    bottom: 0,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '@media (max-width: 768px)': {
      top: '-15px',
    },
  },
  inputRoot: props => ({
    height: '100% !important',
    paddingLeft: 8,
    paddingRight: 8,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiAutocomplete-input': {
      position: 'absolute',
      left: '2px',
      width: '90%',
      top: '21px',
      bottom: 0,
      padding: 0,
      cursor: props.isEnabled ? 'pointer' : 'not-allowed',
      padding: '0 !important',
      marginTop: '5px',
    },
    '& .MuiAutocomplete-inputRoot': {
      flexWrap: 'nowrap !important',
      paddingBottom: '20px !important',
      cursor: 'pointer',
      paddingLeft: '1px !important',
    },
    '& .MuiAutocomplete-endAdornment': {
      transform: 'translateY(-5px)',
      '@media (max-width: 768px)': {
        transform: 'translateY(-8px)',
      },
    },
  }),
  renderTagsText: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: '#19989B',
    fontWeight: 500,
    fontSize: '15px',
  },
  optionText: {
    marginLeft: 4,
    fontSize: '15px !important',
  },
  renderInput: {
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  autocompletePopper: {
    '& .MuiAutocomplete-paper': {
      width: '200px !important',
    },
    '& .MuiAutocomplete-option': {
      paddingLeft: '5px !important',
    },
  },
}));

const ClientsBubble = () => {
  const history = useHistory();
  const isEnabled = ['/home', '/brand-personas/all', '/buyer-personas/all', '/marketing-content/all', '/clients/all'].find(
    e => history.location.pathname.indexOf(e) !== -1,
  );
  const classes = useStyles({ isEnabled });
  const appData = useSelector(({ data }) => data);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  // build dropdown options from ownedAgencyStats
  const dropdownOptions = (appData.ownedAgencyStats || []).map(client => ({
    text: client.name,
    value: client.agencyId,
  }));

  // selected agency ids come from visibleAgencies
  const selectedAgencies = appData.visibleAgencies || [];

  // map selected ids to option objects for Autocomplete's value prop
  const currentValue = dropdownOptions.filter(opt => selectedAgencies.includes(opt.value));

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" style={{ color: '#19989B' }} />;

  const handleSelectChange = (event, newSelectedOptions) => {
    if (appData.loading || appData.loadingAgencyData) return;

    const newSelectedIds = (newSelectedOptions || []).map(o => o.value);

    setOpen(false);

    const oldSelectedIds = appData.visibleAgencies || [];

    const added = newSelectedIds.find(id => !oldSelectedIds.includes(id));
    const removed = oldSelectedIds.find(id => !newSelectedIds.includes(id));

    if (added) {
      dispatch(fetchOwnedAgencyData(added));
    } else if (removed) {
      dispatch(removeOwnedAgencyData(removed));
    }
  };

  if (appData.loading) return null;

  return (
    <Box className={classes.dropdownWrapper}>
      {appData.loadingAgencyData && (
        <div className={classes.loaderOverlay}>
          <CircularProgress size={30} style={{ color: '#19989B' }} />
        </div>
      )}
      <Typography component="div" variant="h4" className={classes.headingText}>
        Clients
      </Typography>
      <FormControl fullWidth disabled={appData.loadingAgencyData || !isEnabled}>
        <Autocomplete
          multiple
          options={dropdownOptions}
          disableCloseOnSelect
          disableClearable
          disabled={appData.loadingAgencyData || !isEnabled}
          getOptionLabel={option => option.text}
          value={currentValue}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={handleSelectChange}
          // when the user types, filter options, but keep selected visible in value
          filterSelectedOptions={false}
          classes={{
            root: classes.inputRoot,
            popper: classes.autocompletePopper,
          }}
          renderOption={(option, { selected }) => (
            <>
              <Checkbox
                icon={icon}
                disabled={appData.loadingAgencyData || !isEnabled}
                checkedIcon={checkedIcon}
                checked={selected}
                classes={{ root: classes.checkboxRoot }}
                size="small"
              />
              <ListItemText primary={<span className={classes.optionText}>{option.text}</span>} />
            </>
          )}
          // renderTags produces a single inline text with ellipsis and styled (teal + bold)
          renderTags={selected => {
            if (!selected || !selected.length) return null;
            // join names with comma, but let CSS ellipsize if too long
            const text = selected.map(s => s.text).join(', ');
            return (
              <span
                style={{
                  cursor: isEnabled ? 'pointer' : 'not-allowed',
                }}
                className={classes.renderTagsText}>
                {text}
              </span>
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              onClick={() => (!appData.loadingAgencyData && isEnabled ? setOpen(true) : null)}
              variant="outlined"
              size="small"
              className={classes.renderInput}
              onKeyDown={event => {
                if (event.key === 'Backspace' && !event.target.value) {
                  event.stopPropagation();
                  event.preventDefault();
                }
              }}
            />
          )}
        />
      </FormControl>
    </Box>
  );
};

export default ClientsBubble;
